import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MicVocal, Music2, PartyPopper } from "lucide-react";
import CarouselRail from "@/components/yts/CarouselRail";
import { useExplore } from "@/lib/ExploreContext";
import { useLang } from "@/lib/i18n";
import { listingPath } from "@/lib/businessSlug";
import { Image } from "@/components/ui/image";

/**
 * Editorial shortlist, not a numerical customer rating.
 * Venue identities and karaoke offerings are independently documented:
 * DEMBE: https://www.dembekaraokebar.com/
 * La Cantina, La Escalera, El Local, Pomarrosa:
 * https://www.plateapr.com/que-hacer/donde-hacer-karaoke-puerto-rico
 * https://www.plateapr.com/que-hacer/lugares-con-karaoke-en-puerto-rico-para-celebrar-tu-cumpleanos
 * La Vergüenza is already linked to karaoke within this guide.
 * Café Borikén was supplied to this guide with karaoke, bomba and billiards.
 *
 * Check the actual venue for current dates. Never invent a schedule, availability
 * or operator endorsement, or send someone to a fictional listing slug.
 */
const PICKS = [
  {
    key: "la-verguenza",
    match: /la verg[uü]enza/i,
    title: "La Vergüenza",
    area: "Condado",
    en: "Chinchorro energy and karaoke nights.",
    es: "Ambiente de chinchorro y noches de karaoke.",
    href: "https://agenda.pr/?category=karaoke&region=metro&save=1",
    type: "venue",
    gradient: "from-[#32191e] via-[#652c3d] to-[#141111]",
  },
  {
    key: "dembe",
    match: /\bdembe\b/i,
    title: "DEMBE Karaoke Bar",
    area: "Calle Loíza · Santurce",
    en: "Karaoke, cocktails and nights with your crew.",
    es: "Karaoke, cócteles y noches con tu corillo.",
    href: "https://www.dembekaraokebar.com/",
    type: "venue",
    gradient: "from-[#292448] via-[#633f72] to-[#131325]",
  },
  {
    key: "la-cantina",
    match: /la cantina karaoke/i,
    title: "La Cantina Karaoke Bar",
    area: "Hato Rey",
    en: "A neighborhood favorite for singing together.",
    es: "Una parada de barrio para cantar en grupo.",
    href: "https://outxout.com/venue/lacantinakaraokebarrestaurantsanjuan",
    type: "venue",
    gradient: "from-[#36252b] via-[#6d3b4b] to-[#171116]",
  },
  {
    key: "la-escalera",
    match: /la escalera/i,
    title: "La Escalera",
    area: "Santurce",
    en: "Cocktails and private-room karaoke.",
    es: "Coctelería y karaoke en salas privadas.",
    href: "https://www.plateapr.com/que-hacer/donde-hacer-karaoke-puerto-rico",
    type: "venue",
    gradient: "from-[#1e2a3b] via-[#354b5e] to-[#14151e]",
  },
  {
    key: "cafe-boriken",
    match: /caf[eé] borik[eé]n/i,
    title: "Café Borikén",
    area: "Río Piedras",
    en: "Bomba, billiards and karaoke under one roof.",
    es: "Bomba, billar y karaoke en un mismo espacio.",
    href: "",
    type: "venue",
    gradient: "from-[#253b2f] via-[#385f53] to-[#121a16]",
  },
  {
    key: "el-local",
    match: /el local en santurce|^el local$/i,
    title: "El Local",
    area: "Santurce",
    en: "Alternative music, local culture and karaoke.",
    es: "Música alternativa, cultura local y karaoke.",
    href: "https://www.plateapr.com/que-hacer/donde-hacer-karaoke-puerto-rico",
    type: "venue",
    gradient: "from-[#25242c] via-[#59414b] to-[#131217]",
  },
  {
    key: "pomarrosa",
    match: /pomarr?osa|museo el pomarrosa/i,
    title: "Pomarrosa",
    area: "Santurce",
    en: "A lively local bar with karaoke tradition.",
    es: "Una barra local con tradición de karaoke.",
    href: "https://www.plateapr.com/que-hacer/lugares-con-karaoke-en-puerto-rico-para-celebrar-tu-cumpleanos",
    type: "venue",
    gradient: "from-[#452333] via-[#763b55] to-[#1d1419]",
  },
  {
    key: "loop-karaoke-bus",
    match: /loop experience karaoke tour bus/i,
    title: "LOOP Karaoke Tour Bus",
    area: "San Juan · Mobile experience",
    en: "Take the sing-along on the road.",
    es: "Llévate el karaoke de paseo.",
    href: "/loop-karaoke-tour-bus",
    type: "experience",
    gradient: "from-[#30213f] via-[#65365d] to-[#14111a]",
  },
];

const photoOf = (item) => item?.cover_image || item?.storefront_image ||
  item?.gallery_images?.find(Boolean) || item?.image || "";
const usable = (item) => item?.id && item?.name && item.status !== "Draft" &&
  item.status !== "Archived" && !item.coming_soon;
const matchName = (left, right) => String(left || "").toLowerCase() === String(right || "").toLowerCase();

function KaraokeCard({ pick, es, index }) {
  const { title, area, photo, to, internal, type, gradient } = pick;
  const label = es ? (type === "experience" ? "EXPERIENCIA DE KARAOKE" : "KARAOKE / GUÍA LOCAL") :
    (type === "experience" ? "KARAOKE EXPERIENCE" : "KARAOKE / LOCAL GUIDE");
  const cta = internal
    ? (es ? "VER LUGAR" : "VIEW SPOT")
    : (es ? "CONSULTAR DETALLES" : "CHECK DETAILS");
  const inner = (
    <>
      <div className="relative aspect-[4/3] overflow-hidden bg-[#211b25]">
        {photo ? (
          <Image src={photo} alt={title} fittingType="fill" quality={76} loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 motion-safe:group-hover:scale-[1.04]" />
        ) : (
          <div className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br ${gradient}`}>
            <div aria-hidden="true" className="absolute inset-3 border border-white/15" />
            <span className="absolute left-4 top-4 font-mono text-[10px] font-bold tracking-[.15em] text-white/65">
              YTS / NIGHTLIFE {String(index + 1).padStart(2, "0")}
            </span>
            {type === "experience" ? <PartyPopper aria-hidden="true" className="h-20 w-20 text-white/75" strokeWidth={1} /> :
              <MicVocal aria-hidden="true" className="h-20 w-20 text-white/75" strokeWidth={1} />}
            <Music2 aria-hidden="true" className="absolute bottom-4 right-4 h-5 w-5 text-white/45" />
          </div>
        )}
        {photo && <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/25" />}
        <span className="absolute bottom-2 left-2 bg-[#191919]/85 px-2 py-1 text-[9px] font-extrabold uppercase tracking-[.1em] text-white">
          {label}
        </span>
      </div>
      <div className="flex min-h-[125px] flex-1 flex-col px-3 pb-3 pt-3">
        <h3 className="line-clamp-2 text-[17px] font-extrabold leading-[1.1] tracking-[-.035em]">{title}</h3>
        <p className="mt-1 truncate text-[10px] font-bold uppercase tracking-[.05em] text-[#a33a33] dark:text-[#ffb2a8]">{area}</p>
        <p className="mt-2 line-clamp-2 text-[12px] leading-[1.4] text-[#777] dark:text-white/65">{es ? pick.es : pick.en}</p>
        <span className="mt-auto flex items-center justify-between gap-2 border-t border-black/10 pt-2 text-[10px] font-extrabold uppercase tracking-[.06em] dark:border-white/20">
          {cta}
          <ArrowUpRight aria-hidden="true" className="h-4 w-4 shrink-0 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </span>
      </div>
    </>
  );
  const cls = "group flex h-full flex-col overflow-hidden rounded-[4px] border border-black/10 bg-white text-[#171717] transition-colors hover:border-black/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#af4235] dark:border-white/20 dark:bg-[#232323] dark:text-white";
  if (internal) return <Link to={to} className={cls}>{inner}</Link>;
  return <a href={to} target="_blank" rel="noopener noreferrer" className={cls}
    aria-label={`${title} — ${cta}, ${es ? "enlace externo" : "external link"}`}>{inner}</a>;
}

export default function HomeKaraokeRail() {
  const { allListings, nbNames } = useExplore();
  const { lang } = useLang();
  const es = lang === "es";
  const spots = useMemo(() => {
    const published = allListings.filter(usable);
    const selectedIds = new Set();
    return PICKS.map((spec) => {
      const found = published.find((item) => spec.match.test(item.name) && !selectedIds.has(item.id));
      if (found) selectedIds.add(found.id);
      const internal = Boolean(found) || spec.href.startsWith("/");
      const to = found ? listingPath(found) : spec.href;
      if (!to) return null; // No invented route for a missing listing.
      return { ...spec, title: found?.name || spec.title, photo: photoOf(found),
        area: found && nbNames[found.neighborhood_id] ? nbNames[found.neighborhood_id] : spec.area,
        to, internal };
    }).filter(Boolean);
  }, [allListings, nbNames]);

  return (
    <div id="home-karaoke" className="scroll-mt-[120px]">
      <CarouselRail
        masthead={es ? "YA TÚ SABES / GUÍA DE KARAOKE" : "YA TÚ SABES / THE KARAOKE EDIT"}
        title={es ? "NOCHES DE KARAOKE." : "KARAOKE NIGHTS."}
        subtitle={es ? "Una selección editorial de lugares para cantar. Confirma los eventos con cada local." :
          "Where to grab the mic. Confirm karaoke nights with each venue."}
        itemWidth="w-[min(67vw,260px)] min-w-[205px] sm:w-[245px] sm:min-w-0"
        action={<Link to="/events" className="hidden items-center gap-1 text-[10px] font-black uppercase text-[#595652] hover:text-[#ad3c32] dark:text-white/80 sm:inline-flex">
          {es ? "Ver eventos" : "Event calendar"} <ArrowUpRight aria-hidden="true" className="h-3.5 w-3.5" />
        </Link>}
      >
        {spots.map((pick, i) => <KaraokeCard key={pick.key} pick={pick} es={es} index={i} />)}
      </CarouselRail>
      <p className="mx-auto max-w-[1240px] px-4 pt-1 text-[10px] leading-[1.45] text-[#777] dark:text-white/60 sm:px-6 lg:px-8">
        {es ? "Las fechas de karaoke y las reservaciones pueden cambiar. Los enlaces externos llevan al local o a una guía de referencia." :
          "Karaoke schedules and reservations may change. External links open the venue or a reference guide."}
      </p>
    </div>
  );
}
